import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Button, Col, Form, Row, Spinner, Alert } from "react-bootstrap";
import { useSelector } from "react-redux";
import { AppContext } from "../context/appContext";
import "./MessageForm.css";
import { API_BASE_URL } from "../apiConfig";
import { getOptimizedAvatar } from "../utils/imageOptimization";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Groups a flat message array by date.
// Passes through status and tempId so the UI can show pending/failed indicators.
function groupMessagesByDate(msgs) {
  const groups = {};
  msgs.forEach((msg) => {
    if (!msg.createdAt) return;
    const dateLabel = new Date(msg.createdAt).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    if (!groups[dateLabel]) groups[dateLabel] = [];
    groups[dateLabel].push({
      _id: msg._id,
      tempId: msg.tempId,
      content: msg.text,
      status: msg.status || "sent",
      time: new Date(msg.createdAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      from: {
        _id: msg.senderId,
        name: msg.senderName,
        picture: msg.senderPicture,
      },
      // Preserve original text so retry can re-emit the right content
      originalText: msg.text,
      // Carry through replyTo snapshot for rendering inline quotes
      replyTo: msg.replyTo || null,
      isEdited: msg.isEdited || false,
      isDeleted: msg.isDeleted || false,
      createdAt: msg.createdAt,
    });
  });
  return Object.keys(groups).map((date) => ({
    _id: date,
    messagesByDate: groups[date],
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

function MessageForm() {
  const [message, setMessage] = useState("");
  const user = useSelector((state) => state.user);
  const { socket, currentRoom, setCurrentRoom, privateMemberMsg } = useContext(AppContext);

  const [flatMessages, setFlatMessages] = useState([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);

  // ── Reply-to state ────────────────────────────────────────────────────
  const [replyingTo, setReplyingTo] = useState(null);
  // Shape: { messageId, senderId, senderName, text }

  // ── Context menu state (hover action menu) ────────────────────────────
  const [contextMenu, setContextMenu] = useState(null);
  // Shape: { messageId } — which message's dropdown is open

  // ── Editing state ─────────────────────────────────────────────────────
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState("");

  // ── Cancelled event redirect alert state ──────────────────────────────
  const [cancelledEventAlert, setCancelledEventAlert] = useState(null);

  const messageContainerRef = useRef(null);
  const isMounted = useRef(true);
  const inputRef = useRef(null);
  // Map of message _id → DOM element ref for scroll-to-original
  const messageRefsMap = useRef({});
  const isInputDisabled = !user || (currentRoom === "room_global_announcements" && user.role !== "admin");

  // ── Scroll-to-original when user clicks a reply quote ─────────────────
  const scrollToMessage = useCallback((messageId) => {
    const el = messageRefsMap.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Add flash highlight
      el.classList.add("message-highlight");
      setTimeout(() => el.classList.remove("message-highlight"), 1500);
    }
  }, []);

  // ── Close context menu on outside click ────────────────────────────────
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenu]);

  // ── Reset reply and editing state when switching rooms ─────────────────
  useEffect(() => {
    setReplyingTo(null);
    setContextMenu(null);
    setEditingMessageId(null);
    setEditingText("");
    setMessage(""); // Clear draft message input box on room change
  }, [currentRoom]);

  // ── Auto-dismiss cancelled event alert ─────────────────────────────────
  useEffect(() => {
    if (cancelledEventAlert) {
      const timer = setTimeout(() => setCancelledEventAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [cancelledEventAlert]);

  // ── 1. Fetch initial messages ───────────────────────────────────────────────
  const fetchInitialMessages = useCallback(async (roomId) => {
    if (!roomId) return;
    setIsLoadingInitial(true);
    setFlatMessages([]);
    try {
      const token = localStorage.getItem("userToken");
      const res = await fetch(`${API_BASE_URL}/api/chat/rooms/${roomId}/messages`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      if (isMounted.current && data.success) {
        setFlatMessages(data.messages.map((m) => ({ ...m, status: "sent" })));
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setTimeout(() => {
          if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    } catch (err) {
      console.error("Error loading messages:", err);
    } finally {
      if (isMounted.current) setIsLoadingInitial(false);
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    if (currentRoom) fetchInitialMessages(currentRoom);
    return () => { isMounted.current = false; };
  }, [currentRoom, fetchInitialMessages]);

  // ── 2. Load older messages ─────────────────────────────────────────────────
  const loadOlderMessages = async () => {
    if (!hasMore || isLoadingMore || !nextCursor || !currentRoom) return;
    setIsLoadingMore(true);
    const container = messageContainerRef.current;
    const previousScrollHeight = container ? container.scrollHeight : 0;
    const previousScrollTop = container ? container.scrollTop : 0;

    try {
      const token = localStorage.getItem("userToken");
      const res = await fetch(
        `${API_BASE_URL}/api/chat/rooms/${currentRoom}/messages?cursor=${encodeURIComponent(nextCursor)}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      const data = await res.json();
      if (isMounted.current && data.success && data.messages.length > 0) {
        setFlatMessages((prev) => [
          ...data.messages.map((m) => ({ ...m, status: "sent" })),
          ...prev,
        ]);
        setHasMore(data.hasMore);
        setNextCursor(data.nextCursor);
        setTimeout(() => {
          if (container) {
            container.scrollTop = previousScrollTop + (container.scrollHeight - previousScrollHeight);
          }
        }, 50);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading older messages:", err);
    } finally {
      if (isMounted.current) setIsLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = messageContainerRef.current;
    if (container && container.scrollTop === 0 && hasMore && !isLoadingMore && !isLoadingInitial) {
      loadOlderMessages();
    }
  };

  // ── 3. Socket Listeners ────────────────────────────────────────────────────
  useEffect(() => {
    // newMessage: only fires for OTHER users' messages.
    // The sender's own messages are confirmed via the ack callback in handleSubmit,
    // so socket.to() on the server excludes the sender from this broadcast.
    const handleNewMessage = (msg) => {
      if (msg.roomId !== currentRoom) return;

      setFlatMessages((prev) => {
        // Self-healing: update stale sender profile data across history
        const healedHistory = prev.map((oldMsg) => {
          if (String(oldMsg.senderId) === String(msg.senderId)) {
            if (oldMsg.senderName !== msg.senderName || oldMsg.senderPicture !== msg.senderPicture) {
              return {
                ...oldMsg,
                senderName: msg.senderName || oldMsg.senderName,
                senderPicture: msg.senderPicture !== undefined ? msg.senderPicture : oldMsg.senderPicture,
              };
            }
          }
          return oldMsg;
        });
        return [...healedHistory, { ...msg, status: "sent" }];
      });

      setTimeout(() => {
        const container = messageContainerRef.current;
        if (container) {
          const isNearBottom = container.scrollHeight - container.clientHeight - container.scrollTop < 150;
          if (isNearBottom) container.scrollTop = container.scrollHeight;
        }
      }, 100);
    };

    const handleRoomDeleted = ({ roomId }) => {
      if (roomId === currentRoom) {
        setCancelledEventAlert("This event and its chat room have been removed by the admin.");
        socket.emit("joinRoom", { roomId: "room_global_general", userId: user?._id });
        setCurrentRoom("room_global_general");
      }
    };

    const handleUserProfileUpdated = ({ userId, name, picture }) => {
      setFlatMessages((prev) =>
        prev.map((msg) => {
          if (String(msg.senderId) === String(userId)) {
            return {
              ...msg,
              senderName: name || msg.senderName,
              senderPicture: picture !== undefined ? picture : msg.senderPicture,
            };
          }
          return msg;
        })
      );
    };

    const handleMessageEdited = ({ messageId, newText, isEdited, updatedAt }) => {
      setFlatMessages((prev) =>
        prev.map((msg) =>
          String(msg._id) === String(messageId)
            ? { ...msg, text: newText, isEdited, updatedAt }
            : msg
        )
      );
    };

    const handleMessageDeleted = ({ messageId, isDeleted }) => {
      setFlatMessages((prev) =>
        prev.map((msg) =>
          String(msg._id) === String(messageId)
            ? { ...msg, text: "This message was deleted", isDeleted, replyTo: null }
            : msg
        )
      );
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("roomDeleted", handleRoomDeleted);
    socket.on("userProfileUpdated", handleUserProfileUpdated);
    socket.on("messageEdited", handleMessageEdited);
    socket.on("messageDeleted", handleMessageDeleted);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("roomDeleted", handleRoomDeleted);
      socket.off("userProfileUpdated", handleUserProfileUpdated);
      socket.off("messageEdited", handleMessageEdited);
      socket.off("messageDeleted", handleMessageDeleted);
    };
  }, [currentRoom, socket, setCurrentRoom, user?._id]);

  // ── 4. Shared ack-based emit helper ────────────────────────────────────────
  // Both handleSubmit and handleRetry delegate to this.
  // The server calls the callback directly for THIS specific emit:
  //   success    → replace the pending placeholder with the confirmed DB message
  //   rate_limit → keep the clock icon and auto-retry silently after 1.2s
  //   other      → mark as failed so the user can click retry manually
  const MAX_AUTO_RETRIES = 10;

  const sendMessageWithRetry = useCallback(
    (text, tempId, replyToPayload = undefined, retryCount = 0) => {
      socket.emit(
        "sendMessage",
        {
          roomId: currentRoom,
          text,
          senderId: user._id,
          ...(replyToPayload && { replyTo: replyToPayload }),
        },
        (response) => {
          if (!isMounted.current) return;

          if (response?.success) {
            setFlatMessages((prev) =>
              prev.map((msg) =>
                msg.tempId === tempId
                  ? { ...response.message, tempId, status: "sent" }
                  : msg,
              ),
            );
            // Scroll to bottom after confirmation
            setTimeout(() => {
              if (messageContainerRef.current) {
                messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
              }
            }, 50);
          } else if (response?.error === "rate_limit" && retryCount < MAX_AUTO_RETRIES) {
            // Rate limited — silently retry after 1.2s, clock stays visible
            setTimeout(() => {
              if (!isMounted.current) return;
              sendMessageWithRetry(text, tempId, replyToPayload, retryCount + 1);
            }, 1200);
          } else {
            // Real failure or max retries exceeded — show retry button
            setFlatMessages((prev) =>
              prev.map((msg) =>
                msg.tempId === tempId ? { ...msg, status: "failed" } : msg,
              ),
            );
          }
        },
      );
    },
    [socket, currentRoom, user?._id],
  );

  // ── Edit Message Handler ──────────────────────────────────────────────
  const handleEditSubmit = (messageId, newText) => {
    socket.emit(
      "editMessage",
      { messageId, roomId: currentRoom, newText },
      (response) => {
        if (!isMounted.current) return;
        if (response?.error) {
          alert("Messages can only be edited within 15 minutes of sending.");
        } else {
          setEditingMessageId(null);
          setEditingText("");
          setMessage("");
        }
      }
    );
  };

  // ── Delete Message Handler ────────────────────────────────────────────
  const handleDeleteSubmit = (messageId) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    socket.emit(
      "deleteMessage",
      { messageId, roomId: currentRoom },
      (response) => {
        if (!isMounted.current) return;
        if (response?.error) {
          alert("Messages can only be deleted within 48 hours of sending.");
        }
      }
    );
  };

  // ── 5. Submit (optimistic or edit) ──────────────────────────────────────────
  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isInputDisabled) return;

    if (editingMessageId) {
      handleEditSubmit(editingMessageId, trimmed);
      return;
    }

    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Build the replyTo payload for the socket (if replying)
    const replyToPayload = replyingTo
      ? {
          messageId: replyingTo.messageId,
          senderId: replyingTo.senderId,
          senderName: replyingTo.senderName,
          text: (replyingTo.text || "").substring(0, 200),
        }
      : undefined;

    const optimisticMsg = {
      _id: tempId,
      tempId,
      roomId: currentRoom,
      senderId: user._id,
      senderName: user.name,
      senderPicture: user.picture || null,
      text: trimmed,
      ...(replyToPayload && { replyTo: replyToPayload }),
      createdAt: new Date().toISOString(),
      status: "pending",
    };

    setFlatMessages((prev) => [...prev, optimisticMsg]);
    setMessage("");
    setReplyingTo(null); // Clear reply state after send

    // Scroll to bottom immediately for the optimistic insert
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      }
    }, 50);

    sendMessageWithRetry(trimmed, tempId, replyToPayload);

    // 10-second safety net: if ack never fires (socket dropped), auto-fail
    setTimeout(() => {
      if (!isMounted.current) return;
      setFlatMessages((prev) =>
        prev.map((msg) =>
          msg.tempId === tempId && msg.status === "pending"
            ? { ...msg, status: "failed" }
            : msg,
        ),
      );
    }, 10000);
  }

  // ── 6. Retry a failed message ──────────────────────────────────────────────
  const handleRetry = useCallback(
    (failedMsg) => {
      const newTempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

      // Flip the failed entry back to pending with a fresh tempId
      setFlatMessages((prev) =>
        prev.map((m) =>
          m.tempId === failedMsg.tempId
            ? { ...m, tempId: newTempId, _id: newTempId, status: "pending" }
            : m,
        ),
      );

      sendMessageWithRetry(failedMsg.originalText, newTempId);

      // Same 10-second safety net
      setTimeout(() => {
        if (!isMounted.current) return;
        setFlatMessages((prev) =>
          prev.map((msg) =>
            msg.tempId === newTempId && msg.status === "pending"
              ? { ...msg, status: "failed" }
              : msg,
          ),
        );
      }, 10000);
    },
    [sendMessageWithRetry],
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  const grouped = groupMessagesByDate(flatMessages);

  return (
    <>
      {cancelledEventAlert && (
        <Alert variant="danger" onClose={() => setCancelledEventAlert(null)} dismissible>
          {cancelledEventAlert}
        </Alert>
      )}
      <div
        className="messages-output"
        ref={messageContainerRef}
        onScroll={handleScroll}
        style={{ overflowY: "auto", maxHeight: "500px", padding: "10px" }}
      >
        {isLoadingInitial ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading room history...</p>
          </div>
        ) : (
          <>
            {isLoadingMore && (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" variant="primary" />
                <span className="ms-2 text-muted">Fetching older messages...</span>
              </div>
            )}

            {user && !privateMemberMsg?._id && (
              <div className="alert alert-info">
                You are in the room:{" "}
                {currentRoom === "room_global_general"
                  ? "General"
                  : currentRoom === "room_global_announcements"
                  ? "Announcements"
                  : "Event Chat"}
              </div>
            )}

            {!user && <div className="alert alert-danger">Please login</div>}

            {user && grouped.length === 0 && (
              <div className="text-center text-muted py-5">
                {isInputDisabled
                  ? "No messages yet."
                  : "No messages yet. Say hello!"}
              </div>
            )}

            {user &&
              grouped.map(({ _id: date, messagesByDate }, idx) => (
                <div key={idx}>
                  <p className="alert alert-info text-center message-date-indicator">{date}</p>
                  {messagesByDate?.map((msgItem) => {
                    const { content, time, from: sender, status, tempId: mTempId, replyTo: msgReplyTo } = msgItem;
                    const isMe = String(sender?._id) === String(user?._id);
                    const isPending = status === "pending";
                    const isFailed = status === "failed";
                    const msgId = mTempId || msgItem._id;
                    const isMenuOpen = contextMenu?.messageId === msgId;

                    return (
                      <div
                        className={isMe ? "message" : "incoming-message"}
                        key={msgId}
                        ref={(el) => { if (el) messageRefsMap.current[msgItem._id] = el; }}
                        style={{ opacity: isPending ? 0.7 : 1 }}
                      >
                        <div className="message-inner message-wrapper">
                          {/* ── Hover Action Button + Context Menu ── */}
                          {!msgItem.isDeleted && (
                            <div className="message-actions">
                              <button
                                className="message-actions-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setContextMenu(isMenuOpen ? null : { messageId: msgId });
                                }}
                                title="Actions"
                              >
                                ▾
                              </button>
                              {isMenuOpen && (
                                <div className="message-context-menu" onClick={(e) => e.stopPropagation()}>
                                  <button
                                    className="context-menu-item"
                                    onClick={() => {
                                      setReplyingTo({
                                        messageId: msgItem._id,
                                        senderId: sender._id,
                                        senderName: sender.name,
                                        text: msgItem.originalText || content,
                                      });
                                      setEditingMessageId(null);
                                      setEditingText("");
                                      setMessage("");
                                      setContextMenu(null);
                                      // Focus the input field
                                      setTimeout(() => inputRef.current?.focus(), 50);
                                    }}
                                  >
                                    ↩ Reply
                                  </button>
                                  <div className="context-menu-divider" />
                                  <button
                                    className="context-menu-item"
                                    onClick={() => {
                                      navigator.clipboard.writeText(msgItem.originalText || content);
                                      setContextMenu(null);
                                    }}
                                  >
                                    📋 Copy Text
                                  </button>
                                  {(() => {
                                    const FIFTEEN_MINUTES = 15 * 60 * 1000;
                                    const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;
                                    const isSender = String(sender?._id) === String(user?._id);
                                    const isAdmin = user?.role === 'admin' || user?.name === 'Admin';
                                    const messageAge = Date.now() - new Date(msgItem.createdAt || Date.now()).getTime();

                                    const canEdit = isSender && !msgItem.isDeleted && messageAge <= FIFTEEN_MINUTES;
                                    const canDelete = !msgItem.isDeleted && (isAdmin || (isSender && messageAge <= FORTY_EIGHT_HOURS));

                                    return (
                                      <>
                                        {canEdit && (
                                          <>
                                            <div className="context-menu-divider" />
                                            <button
                                              className="context-menu-item"
                                              onClick={() => {
                                                setEditingMessageId(msgItem._id);
                                                setEditingText(msgItem.originalText || content);
                                                setMessage(msgItem.originalText || content);
                                                setReplyingTo(null);
                                                setContextMenu(null);
                                                setTimeout(() => inputRef.current?.focus(), 50);
                                              }}
                                            >
                                              ✏️ Edit Message
                                            </button>
                                          </>
                                        )}
                                        {canDelete && (
                                          <>
                                            <div className="context-menu-divider" />
                                            <button
                                              className="context-menu-item text-danger"
                                              onClick={() => {
                                                handleDeleteSubmit(msgItem._id);
                                                setContextMenu(null);
                                              }}
                                            >
                                              🗑️ Delete Message
                                            </button>
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Sender Header ── */}
                          <div className="d-flex align-items-center mb-3">
                            <img
                              src={getOptimizedAvatar(sender.picture, 35) || "/assets/icon.png"}
                              style={{
                                width: 35,
                                height: 35,
                                objectFit: "cover",
                                borderRadius: "50%",
                                marginRight: 10,
                              }}
                              alt="avatar"
                            />
                            <p className="message-sender">{isMe ? "You" : sender.name}</p>
                          </div>

                          {/* ── Reply Quote Card (if this message is a reply) ── */}
                          {msgReplyTo && msgReplyTo.messageId && (
                            <div
                              className="reply-quote"
                              onClick={() => scrollToMessage(msgReplyTo.messageId)}
                            >
                              <div className="reply-quote-border" />
                              <div className="reply-quote-content">
                                <p className="reply-quote-sender">{msgReplyTo.senderName || "Unknown"}</p>
                                <p className="reply-quote-text">{msgReplyTo.text || ""}</p>
                              </div>
                            </div>
                          )}

                          {/* ── Message Content (or Tombstone) ── */}
                          {msgItem.isDeleted ? (
                            <p className="message-content text-muted font-italic">
                              🚫 This message was deleted
                            </p>
                          ) : (
                            <p className="message-content">{content}</p>
                          )}
                          <div className="d-flex align-items-center gap-2">
                            <p className="message-timestamp-left mb-0">
                              {time}
                              {msgItem.isEdited && (
                                <span className="text-muted font-italic ms-1" style={{ fontSize: "11px" }}>
                                  (edited)
                                </span>
                              )}
                            </p>
                            {isMe && isPending && (
                              <span title="Sending…" style={{ fontSize: 11, color: "#9CA3AF" }}>
                                🕐
                              </span>
                            )}
                            {isMe && isFailed && (
                              <button
                                onClick={() => handleRetry(msgItem)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#ef4444",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                                title="Failed — click to retry"
                              >
                                ⚠ Retry
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
          </>
        )}
      </div>

      {/* ── Reply Preview Banner ── */}
      {replyingTo && (
        <div className="reply-preview-bar">
          <div className="reply-preview-content">
            <p className="reply-preview-label">
              ↩ Replying to {replyingTo.senderName || "Unknown"}
            </p>
            <p className="reply-preview-text">
              {(replyingTo.text || "").substring(0, 100)}
            </p>
          </div>
          <button
            className="reply-preview-close"
            onClick={() => setReplyingTo(null)}
            title="Cancel reply"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Editing Message Preview Banner ── */}
      {editingMessageId && (
        <div className="reply-preview-bar" style={{ borderLeft: "3px solid #3b82f6" }}>
          <div className="reply-preview-content">
            <p className="reply-preview-label" style={{ color: "#3b82f6" }}>
              ✏️ Editing Message
            </p>
            <p className="reply-preview-text">
              {editingText.substring(0, 100)}
            </p>
          </div>
          <button
            className="reply-preview-close"
            onClick={() => {
              setEditingMessageId(null);
              setEditingText("");
              setMessage("");
            }}
            title="Cancel edit"
          >
            ✕
          </button>
        </div>
      )}

      <Form onSubmit={handleSubmit} className="mt-3">
        <Row>
          <Col md={11}>
            <Form.Group>
              <Form.Control
                ref={inputRef}
                type="text"
                placeholder={
                  currentRoom === "room_global_announcements" && user?.role !== "admin"
                    ? "Only admins can post in Announcements"
                    : "Type a message..."
                }
                disabled={isInputDisabled}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Button
              variant="primary"
              type="submit"
              style={{ width: "100%", backgroundColor: "orange" }}
              disabled={isInputDisabled || !message.trim()}
            >
              <i className="fas fa-paper-plane"></i>
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}

export default MessageForm;
